/* src/App.js (Corrected with Search Route in Layout) */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, Outlet } from 'react-router-dom';
import WebViewDetector from './components/WebViewDetector';

// --- Helper & Auth Component Imports ---
import UserAuthWrapper from './components/UserAuthWrapper';
import { ProtectedRoute, AdminOnlyRoute } from './components/ProtectedRoute';
import GuestPromptWrapper from './components/GuestPromptWrapper';
import Menu from './components/Menu';

// --- Page Imports ---
import Home from './pages/Home';
import Resources from './pages/Resources';
import Profile from './pages/Profile';
import PublicProfilePage from './pages/PublicProfilePage';
import MarathonProfile from './pages/MarathonProfile';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import MyBookmarksPage from './pages/MyBookmarksPage';
import MyCoursesPage from './pages/courses/MyCoursesPage';
import SearchPage from './pages/SearchPage';
import UnsubscribePage from './pages/UnsubscribePage';
import AiChat from './components/AiChat';

// --- Content and Admin Imports ---
import Articles from './pages/articles/Articles';
import ArticleListPage from './pages/articles/ArticleListPage';
import ArticleDetailPage from './pages/articles/ArticleDetailPage';
import Videos from './pages/videos/Videos';
import VideoListPage from './pages/videos/VideosListPage';
import VideoPlayerPage from './pages/videos/VideoPlayerPage';
import Reels from './pages/reels/Reels';
import ReelListPage from './pages/reels/ReelListPage';
import ReelPlayerPage from './pages/reels/ReelPlayerPage';
import Podcasts from './pages/podcasts/Podcasts';
import PodcastListPage from './pages/podcasts/PodcastListPage';
import PodcastPlayerPage from './pages/podcasts/PodcastPlayerPage';
import Courses, { CoursesLayout } from './pages/courses/Courses';
import CoursePlayerPage from './pages/courses/CoursePlayerPage';

// --- ADMIN IMPORTS ---
import AdminPage from './pages/admin/AdminPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminArticleManager from './pages/admin/AdminArticleManager';
import AdminVideoManager from './pages/admin/AdminVideoManager';
import AdminReelManager from './pages/admin/AdminReelManager';
import AdminPodcastManager from './pages/admin/AdminPodcastManager';
import AdminUserManager from './pages/admin/AdminUserManager';
import AdminCommentManager from './pages/admin/AdminCommentManager';
import AdminCategoryManager from './pages/admin/AdminCategoryManager';
import AdminCourseManager from './pages/admin/AdminCourseManager';
import AdminCourseEditor from './pages/admin/AdminCourseEditor';
import AdminLiveCourseManager from './pages/admin/AdminLiveCourseManager';
import AdminRawThoughtManager from './pages/admin/AdminRawThoughtManager';
import AdminBookManager from './pages/admin/AdminBookManager';

// --- LIVE COURSE IMPORTS ---
import LiveCourseDetailPage from './pages/courses/LiveCourseDetailPage';
import LiveCoursePaymentPage from './pages/courses/LiveCoursePaymentPage';
import LiveCourseRegistrationCompletePage from './pages/courses/RegistrationCompletePage';

import BookPreviewPage from './pages/BookPreviewPage';
import CustomizedBookPage from './pages/book/components/CustomizedBookPage';
import GeneratedChapterPage from './pages/book/components/GeneratedChapterPage';

import Navbar from './components/Navbar';
import Header from './components/Header';

const ThemeInitializer = () => {
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'light';
        document.body.className = '';
        document.body.classList.add(`${savedTheme}-mode`);
    }, []);
    return null;
};

const AppLayout = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    let title = "Hemant Jha";
    let showBackButton = true;

    const path = location.pathname;
    if (path === '/' || path.startsWith('/home')) {
        title = "Hemant Jha";
    } else if (path.startsWith('/search')) {
        title = "Search Results";
    } else if (path.startsWith('/profile')) {
        title = "About Me";
    } else if (path.startsWith('/marathon-profile')) {
        title = "Marathon Diaries";
    } else if (path.startsWith('/settings')) {
        title = "Settings";
    } else if (path.startsWith('/resources')) {
        title = "Resources";
    } else if (path.startsWith('/courses') || path.startsWith('/live-courses') || path.startsWith('/my-courses')) {
        title = "Courses";
    } else if (path.startsWith('/articles')) {
        title = "Articles";
    } else if (path.startsWith('/videos')) {
        title = "Videos";
    } else if (path.startsWith('/reels')) {
        title = "Reels";
    } else if (path.startsWith('/podcasts')) {
        title = "Podcasts";
    } else if (path.startsWith('/ai-chat')) {
        title = "AI Assistant";
    } else if (path.startsWith('/customized-book')) {
        title = "Your Personalized Book";
    }
    
    if (path === '/' || path === '/home.html') {
        showBackButton = false;
    }

    // Hide main header for AI chat page, but keep navbar
    const isAiChatPage = path.startsWith('/ai-chat');

    return (
        <div className="app-container">
            <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
            {!isAiChatPage && <Header title={title} showBackButton={showBackButton} />}
            <main className={`page-padding ${isAiChatPage ? 'ai-chat-page' : ''}`}>
                <Outlet />
            </main>
            <Navbar onMenuOpen={() => setIsMenuOpen(true)} />
        </div>
    );
};

function App() {
  return (
    <WebViewDetector>
      <Router>
        <ThemeInitializer />
        <Routes>
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="articles" element={<AdminArticleManager />} />
                <Route path="raw-thoughts" element={<AdminRawThoughtManager />} />
                <Route path="videos" element={<AdminVideoManager />} />
                <Route path="reels" element={<AdminReelManager />} />
                <Route path="books" element={<AdminBookManager />} />
                <Route path="podcasts" element={<AdminPodcastManager />} />
                <Route path="users" element={<AdminUserManager />} />
                <Route path="comments" element={<AdminCommentManager />} />
                <Route path="categories" element={<AdminCategoryManager />} />
                <Route path="courses" element={<AdminCourseManager />} />
                <Route path="courses/:courseId" element={<AdminCourseEditor />} />
                <Route path="live-courses" element={<AdminLiveCourseManager />} />
            </Route>

            <Route path="/login" element={<LoginPage />} />
            <Route path="/live-courses/registration-complete" element={<LiveCourseRegistrationCompletePage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />

            {/* Admin-only courses route with layout - must be before the main layout route */}
            <Route path="/courses" element={
                <AdminOnlyRoute>
                    <CoursesLayout>
                        <Courses />
                    </CoursesLayout>
                </AdminOnlyRoute>
            } />

            {/* Routes that need authentication but DO NOT have the main layout */}
            <Route element={<UserAuthWrapper />}>
                <Route path="/courses/:courseId" element={<CoursePlayerPage />} />
                <Route path="/my-bookmarks" element={<MyBookmarksPage />} />
                <Route path="/live-courses/:courseId/payment" element={<LiveCoursePaymentPage />} />
                <Route path="book-preview" element={<BookPreviewPage />} />
            </Route>

            {/* Routes that HAVE the main layout */}
            <Route path="/" element={<AppLayout />}>
                <Route index element={<Home />} />
                <Route path="home.html" element={<Home />} />
                <Route path="search" element={<SearchPage />} />

                {/* Public pages with layout */}
                <Route path="/live-courses/:courseId" element={<LiveCourseDetailPage />} />
                <Route element={<GuestPromptWrapper />}>
                    <Route path="resources.html" element={<Resources />} />
                    <Route path="articles" element={<Articles />} />
                    <Route path="articles/:id" element={<ArticleDetailPage />} />
                    <Route path="articles/category/:categoryName" element={<ArticleListPage />} />
                    <Route path="videos" element={<Videos />} />
                    <Route path="videos/:id" element={<VideoPlayerPage />} />
                    <Route path="videos/category/:categoryName" element={<VideoListPage />} />
                    <Route path="reels" element={<Reels />} />
                    <Route path="reels/:id" element={<ReelPlayerPage />} />
                    <Route path="reels/category/:categoryName" element={<ReelListPage />} />
                    <Route path="podcasts" element={<Podcasts />} />
                    <Route path="podcasts/:id" element={<PodcastPlayerPage />} />
                    <Route path="podcasts/category/:categoryName" element={<PodcastListPage />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="marathon-profile" element={<MarathonProfile />} />
                </Route>

                {/* Authenticated pages with layout */}
                <Route element={<UserAuthWrapper />}>
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="my-courses" element={<MyCoursesPage />} />
                    <Route path="ai-chat" element={<AiChat />} />
                    <Route path="book-preview" element={<BookPreviewPage />} />
                    <Route path="customized-book" element={<CustomizedBookPage />} />
                    <Route path="customized-book/:bookId" element={<CustomizedBookPage />} />
                    <Route path="generate-chapter/:bookId/:chapterId" element={<GeneratedChapterPage />} />
                </Route>
            </Route>
        </Routes>
      </Router>
    </WebViewDetector>
  );
}

export default App;
